import { api } from './api';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  fullName: string;
  email: string;
  password: string;
  bio?: string;
  location?: string;
  website?: string;
}

export interface AuthResponse {
  user: {
    id: string;
    fullName: string;
    email: string;
    avatar?: string;
    subscription: string;
    enabledFeatures?: string[];
    joinedDate: string;
    authProvider?: 'password' | 'google';
  };
  access_token: string;
  refresh_token: string;
}

export interface User {
  id: string;
  fullName: string;
  email: string;
  bio?: string;
  location?: string;
  website?: string;
  avatar?: string;
  googleId?: string;
  subscription: string;
  enabledFeatures?: string[];
  preferences: {
    theme: string;
    language: string;
    notifications: {
      emailUpdates: boolean;
      practiceReminders: boolean;
      achievementNotifications: boolean;
      weeklyProgress: boolean;
    };
  };
  stats: {
    totalSolved: number;
    easySolved: number;
    mediumSolved: number;
    hardSolved: number;
    currentStreak: number;
    bestStreak: number;
    totalTimeSpent: number;
    practiceProblems: number;
    testsCompleted: number;
    totalScore: number;
    completedTopics: string[];
    achievements: string[];
  };
  isActive: boolean;
  isEmailVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

class AuthService {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await api.post('/auth/login', credentials);

    // Store tokens
    localStorage.setItem('access_token', response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);
    localStorage.setItem('user', JSON.stringify(response.user));

    return response;
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await api.post('/auth/register', userData);

    // Store tokens
    localStorage.setItem('access_token', response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);
    localStorage.setItem('user', JSON.stringify(response.user));

    return response;
  }

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Even if the API call fails, we should still clear local storage
      console.error('Logout API call failed:', error);
    } finally {
      // Clear local storage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  }

  async refreshToken(): Promise<{ access_token: string }> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post('/auth/refresh', { refresh_token: refreshToken });

    // Update access token
    localStorage.setItem('access_token', response.access_token);

    return response;
  }

  async getProfile(): Promise<User> {
    return await api.get('/auth/me');
  }

  async updateProfile(userData: Partial<User>): Promise<User> {
    const response = await api.patch('/users/me', userData);

    // Update stored user data
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      const updatedUser = { ...currentUser, ...response };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }

    return response;
  }

  async updatePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    return await api.patch('/users/me/password', {
      currentPassword,
      newPassword,
    });
  }

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  async getDashboard(): Promise<any> {
    return await api.get('/users/me/dashboard');
  }

  async loginWithGoogle(idToken: string): Promise<AuthResponse> {
    const response = await api.post('/auth/google', { idToken });

    // Store tokens
    localStorage.setItem('access_token', response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);
    localStorage.setItem('user', JSON.stringify(response.user));

    return response;
  }
}

export const authService = new AuthService();
export default authService;
